import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Send, MessageCircle, AlertCircle } from 'lucide-react';

/**
 * ChatPreview - Modal flotante de chat (SOLO UI PREVIEW)
 * No tiene integración real con WebSocket/backend.
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - otherPartyName: string (nombre del taller o cliente)
 *  - otherPartyRole: 'taller' | 'client'
 *  - contextInfo: string (e.g., "Cambio de aceite - Toyota Corolla")
 */
const ChatPreview = ({ isOpen, onClose, otherPartyName, otherPartyRole, contextInfo }) => {
  const [messages, setMessages] = useState([
    // Mock initial messages
    {
      id: '1',
      sender: 'other',
      text: '¡Hola! Gracias por tu interés en nuestra oferta. ¿Tienes alguna pregunta?',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: '2',
      sender: 'me',
      text: 'Sí, ¿incluye el filtro de aceite?',
      timestamp: new Date(Date.now() - 3500000).toISOString(),
    },
    {
      id: '3',
      sender: 'other',
      text: 'Claro que sí, el precio incluye aceite sintético y filtro original.',
      timestamp: new Date(Date.now() - 3400000).toISOString(),
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const handleSend = () => {
    if (!newMessage.trim()) return;
    
    // Add user message
    const userMsg = {
      id: Date.now().toString(),
      sender: 'me',
      text: newMessage,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setNewMessage('');

    // Simulate typing indicator then mock response
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const mockReply = {
        id: (Date.now() + 1).toString(),
        sender: 'other',
        text: getRandomReply(),
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, mockReply]);
    }, 1500);
  };

  const getRandomReply = () => {
    const replies = [
      '¡Perfecto! Estamos para servirte.',
      'Claro, con gusto te ayudo con eso.',
      '¿Hay algo más en lo que pueda asistirte?',
      'Excelente, quedamos pendientes entonces.',
      'Por supuesto, cualquier duda me avisas.',
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  };

  const formatTime = (iso) => {
    return new Date(iso).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end p-4 pointer-events-none">
      {/* Overlay for mobile - optional */}
      <div 
        className="fixed inset-0 bg-black/20 pointer-events-auto md:hidden"
        onClick={onClose}
      />
      
      {/* Chat Modal */}
      <Card 
        className="w-full max-w-sm bg-white shadow-2xl rounded-xl overflow-hidden pointer-events-auto flex flex-col"
        style={{ height: '500px', maxHeight: '80vh' }}
        data-testid="chat-preview-modal"
      >
        {/* Header */}
        <div className="bg-purple-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">{otherPartyName || 'Chat'}</h3>
                <p className="text-xs text-purple-200">
                  {otherPartyRole === 'taller' ? 'Taller' : 'Cliente'}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="text-white hover:bg-white/20 p-1 h-auto"
              data-testid="close-chat-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          {contextInfo && (
            <p className="text-xs text-purple-200 mt-2 truncate">{contextInfo}</p>
          )}
        </div>

        {/* Preview Banner */}
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-800">
            Vista preliminar — integración en tiempo real próximamente
          </p>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  msg.sender === 'me'
                    ? 'bg-purple-600 text-white rounded-br-md'
                    : 'bg-white text-slate-800 shadow-sm rounded-bl-md'
                }`}
              >
                <p className="text-sm">{msg.text}</p>
                <p className={`text-xs mt-1 ${msg.sender === 'me' ? 'text-purple-200' : 'text-slate-400'}`}>
                  {formatTime(msg.timestamp)}
                </p>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white text-slate-600 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-3 bg-white border-t">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Escribe un mensaje..."
              className="flex-1 px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="chat-input"
            />
            <Button 
              onClick={handleSend}
              className="rounded-full w-10 h-10 p-0 bg-purple-600 hover:bg-purple-700"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChatPreview;
